import { SourcesListResponseSectionTypename } from '../../../__generated__/chirp/com/hubspot/copilot/rpc/v2/CopilotMessageService';
import { reportError } from '../reportError';
import { AppCitationTypename, convertToSearchResultMarkers, createCiteMarkerRegex } from './citationUtils';
function asArray(value) {
  return Array.isArray(value) ? value : [];
}
function buildCitationIdToEntryMap(messages) {
  return new Map(messages.flatMap(msg => msg.blocks).filter(block => block.type === 'DISPLAY_ONLY').flatMap(block => {
    var _block$entries;
    return (_block$entries = block.entries) !== null && _block$entries !== void 0 ? _block$entries : [];
  }).filter(entry => typeof entry.citationId === 'string').map(entry => [entry.citationId, entry]));
}

// Two delivery paths set __typename differently:
// - BZE citationId path: toDisplayOnlyEntry maps the `type` field value 'SOURCES_LIST' to
//   the FQ typename via BZE_SHORT_TYPE_TO_TYPENAME, so the first check handles it.
// - Ably list path: the plain-JSON __typename is 'SourcesListResponseSection' (a partial
//   class name). toDisplayOnlyEntry picks it up directly from getString(value, '__typename')
//   before reaching the `type` fallback, so BZE_SHORT_TYPE_TO_TYPENAME never fires and the
//   entry keeps the short name. value.type === 'SOURCES_LIST' is the fallback for this path.
function isSourcesListEntry(entry) {
  if (entry.__typename === SourcesListResponseSectionTypename) return true;
  const value = entry.value;
  return (value === null || value === void 0 ? void 0 : value.type) === 'SOURCES_LIST';
}

// Returns the sourcesSections arrays from a DISPLAY_ONLY block, handling two delivery paths:
//   1. entries path: block has decoded entries (CHIRP history and Ably streaming both land
//      here) — match by SOURCES_LIST shape so only sources-list entries contribute
//   2. text path: parseDisplayOnlyBlock could not type the value and stored it as block.text
function getSourcesSectionsFromBlock(block) {
  var _block$entries2;
  const fromEntries = ((_block$entries2 = block.entries) !== null && _block$entries2 !== void 0 ? _block$entries2 : []).filter(isSourcesListEntry).flatMap(entry => {
    var _entry$value;
    return asArray((_entry$value = entry.value) === null || _entry$value === void 0 ? void 0 : _entry$value.sourcesSections);
  });
  if (fromEntries.length > 0) return fromEntries;
  if (!block.text) return [];
  try {
    const parsed = JSON.parse(block.text);
    return asArray(parsed === null || parsed === void 0 ? void 0 : parsed.sourcesSections);
  } catch (error) {
    reportError(error, {
      scope: 'resolve-citations'
    });
    return [];
  }
}

// Extracts BreezeEngineCitations from SourcesListResponseSection display blocks.
// Using this as the primary citation source is more reliable than parsing the
// text-block JSON because display blocks are never substituted by large-output
// file handling (only the TextChirpContentBlock is replaced with a file-path
// message when a tool result exceeds the 15k-token threshold).
export function extractCitationsFromSourcesList(messages) {
  return messages.flatMap(msg => msg.blocks).filter(block => block.type === 'DISPLAY_ONLY').flatMap(getSourcesSectionsFromBlock).flatMap(section => asArray(section === null || section === void 0 ? void 0 : section.sources)).flatMap(item => {
    var _source$data;
    const source = item;
    if (typeof source.id !== 'string' || !source.id.startsWith('cite-')) return [];
    const data = source.data;
    const fileUrl = data === null || data === void 0 ? void 0 : data.fileUrl;
    if ((data === null || data === void 0 ? void 0 : data.source) === 'FILE_UPLOAD' && typeof fileUrl === 'string' && fileUrl) {
      return [{
        id: source.id,
        fileUrl,
        title: typeof source.primaryText === 'string' ? source.primaryText : source.id,
        citationType: 'FileUploadCitation'
      }];
    }
    const url = (_source$data = source.data) === null || _source$data === void 0 ? void 0 : _source$data.url;
    if (typeof url !== 'string' || !url) return [];
    return [{
      id: source.id,
      url,
      title: typeof source.primaryText === 'string' ? source.primaryText : url,
      citationType: 'WebPageCitation'
    }];
  });
}
function resolveInlineEntries(text, entryMap) {
  const entries = [];
  const seen = new Map();
  let nextIndex = 1;
  const resolved = text.replace(createCiteMarkerRegex(), match => {
    const id = match.slice(1, -1);
    if (seen.has(id)) return `[RESPONSE_SECTION_${seen.get(id)}]`;
    const entry = entryMap.get(id);
    if (!entry) return match;
    const index = nextIndex++;
    seen.set(id, index);
    entries.push(entry);
    return `[RESPONSE_SECTION_${index}]`;
  });
  return {
    text: resolved,
    entries
  };
}
export function resolveCitations(messages, preExtractedCitations = []) {
  // Always prefer citations from SourcesListResponseSection display blocks:
  // they survive large-output file substitution, unlike text-block JSON.
  // Last writer wins: later SourcesListResponseSection entries (e.g. a second
  // search call that returns updated results) overwrite earlier ones for the
  // same cite-ID. Fill any remaining IDs from preExtractedCitations (text-block
  // JSON path) so tools that don't emit a SourcesListResponseSection still work.
  const entryMap = buildCitationIdToEntryMap(messages);
  const sourcesListById = new Map(extractCitationsFromSourcesList(messages).map(c => [c.id, c]));

  // Per-citation display blocks store the original Citation object (e.g.
  // WebPageCitation) whose title is always the document title. Prefer that
  // title over SourceItem.primaryText, which may be set to the app name
  // (e.g. "Google Drive") after a future BE field-assignment change.
  entryMap.forEach((entry, id) => {
    var _entry$value2;
    const citation = sourcesListById.get(id);
    const blockTitle = (_entry$value2 = entry.value) === null || _entry$value2 === void 0 ? void 0 : _entry$value2.title;
    if (citation && typeof blockTitle === 'string' && blockTitle) {
      sourcesListById.set(id, Object.assign({}, citation, {
        title: blockTitle
      }));
    }
  });
  const allCitations = [...sourcesListById.values(), ...preExtractedCitations.filter(c => !sourcesListById.has(c.id))];
  if (allCitations.length === 0 && entryMap.size === 0) return messages;

  // Chip citations must reach the section formatter so chips render with
  // popover data when the LLM embeds [CRM_RECORD:...] markers in composable
  // card body values without referencing [cite-xxx] in prose. To prevent
  // cross-turn bleed, each chip citation is attached to at most one ASSISTANT
  // text block — the first one that references or needs it.
  const chipCitations = preExtractedCitations.filter(citation => citation.displayType === 'RECORD_CHIP');
  const consumedChipIds = new Set();
  const consumedCitationIds = new Set();
  if (entryMap.size > 0) {
    for (const msg of messages) {
      if (msg.role !== 'ASSISTANT') continue;
      for (const block of msg.blocks) {
        var _block$text$match;
        if (block.type !== 'TEXT') continue;
        const markers = (_block$text$match = block.text.match(createCiteMarkerRegex())) !== null && _block$text$match !== void 0 ? _block$text$match : [];
        for (const marker of markers) {
          const citationId = marker.slice(1, -1);
          if (entryMap.has(citationId)) {
            consumedCitationIds.add(citationId);
          }
        }
      }
    }
  }
  let anyChanged = false;
  const result = messages.map(msg => {
    if (msg.role !== 'ASSISTANT') return msg;
    let changed = false;
    const newBlocks = [];
    for (const block of msg.blocks) {
      if (block.type === 'TEXT') {
        const hasCiteMarkers = createCiteMarkerRegex().test(block.text);
        const freshChipCitations = chipCitations.filter(chip => !consumedChipIds.has(chip.id));
        if (!hasCiteMarkers) {
          if (freshChipCitations.length === 0) {
            newBlocks.push(block);
            continue;
          }
          // No inline cite markers, but attach fresh chip citations so
          // crmRecordChips is populated for any [CRM_RECORD:...] markers in
          // associated sections.
          freshChipCitations.forEach(chip => consumedChipIds.add(chip.id));
          changed = true;
          anyChanged = true;
          newBlocks.push(Object.assign({}, block, {
            citations: freshChipCitations
          }));
          continue;
        }
        const {
          text: afterCitations,
          citations: resolvedCitations
        } = convertToSearchResultMarkers(block.text, allCitations);
        const {
          text: finalText,
          entries: inlineEntries
        } = resolveInlineEntries(afterCitations, entryMap);

        // Append fresh chip citations not already produced by convertToSearchResultMarkers
        // (chips whose [cite-xxx] appeared in prose are already in resolvedCitations).
        const extraChipCitations = freshChipCitations.filter(chip => !resolvedCitations.some(rc => rc.id === chip.id));
        const blockCitations = [...resolvedCitations, ...extraChipCitations];
        blockCitations.forEach(c => {
          if (c.displayType === 'RECORD_CHIP') consumedChipIds.add(c.id);
        });
        if (blockCitations.length === 0 && inlineEntries.length === 0) {
          newBlocks.push(block);
          continue;
        }
        changed = true;
        anyChanged = true;
        newBlocks.push(Object.assign({}, block, {
          text: finalText
        }, blockCitations.length > 0 ? {
          citations: blockCitations
        } : {}, inlineEntries.length > 0 ? {
          entries: inlineEntries
        } : {}));
      } else if (block.type === 'DISPLAY_ONLY' && entryMap.size > 0) {
        var _block$entries3, _block$entries4;
        const remainingEntries = ((_block$entries3 = block.entries) !== null && _block$entries3 !== void 0 ? _block$entries3 : []).filter(entry => {
          if (entry.citationId && consumedCitationIds.has(entry.citationId)) return false;
          if (entry.__typename === AppCitationTypename && entry.citationId) return false;
          return true;
        });
        if (remainingEntries.length === ((_block$entries4 = block.entries) !== null && _block$entries4 !== void 0 ? _block$entries4 : []).length) {
          newBlocks.push(block);
          continue;
        }
        changed = true;
        anyChanged = true;
        if (remainingEntries.length > 0 || block.text !== undefined) {
          newBlocks.push(Object.assign({}, block, {
            entries: remainingEntries
          }));
        }
      } else {
        newBlocks.push(block);
      }
    }
    if (!changed) return msg;
    return Object.assign({}, msg, {
      blocks: newBlocks
    });
  });
  return anyChanged ? result : messages;
}